import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Disc, Music, Pause, Play, Shuffle, Users, Volume2, VolumeX } from 'lucide-react';

const STREAM_URL = '/stream';
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000];
const STALL_GRACE_MS = 12000;

export default function Player() {
  const [currentSong, setCurrentSong] = useState(null);
  const [nextSongs, setNextSongs] = useState([]);
  const [totalSongs, setTotalSongs] = useState(0);
  const [shuffle, setShuffle] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(() => Number.parseInt(localStorage.getItem('radio_vol') || '75', 10));
  const [isMuted, setIsMuted] = useState(false);
  const [streamState, setStreamState] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('Toca reproducir para escuchar');

  const audioRef = useRef(null);
  const wantedPlayingRef = useRef(false);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const reconnectRef = useRef(null);
  const lastProgressAtRef = useRef(Date.now());
  const lastMediaTimeRef = useRef(0);

  const clearRetry = useCallback(() => {
    if (retryTimerRef.current) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const reconnect = useCallback((reason = 'Recuperando la señal') => {
    if (!wantedPlayingRef.current || !audioRef.current) return;

    clearRetry();
    const attempt = retryCountRef.current;
    const delay = RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)];
    retryCountRef.current += 1;
    setStreamState(navigator.onLine ? 'reconnecting' : 'offline');
    setStatusMessage(navigator.onLine ? `${reason}…` : 'Sin conexión. Reintentaremos automáticamente.');

    retryTimerRef.current = window.setTimeout(async () => {
      if (!wantedPlayingRef.current || !audioRef.current || !navigator.onLine) return;
      const audio = audioRef.current;
      try {
        setStreamState('connecting');
        setStatusMessage('Conectando con Radio ASENF…');
        audio.src = `${STREAM_URL}?reconnect=${Date.now()}`;
        audio.load();
        await audio.play();
      } catch (error) {
        if (error?.name === 'NotAllowedError') {
          wantedPlayingRef.current = false;
          setIsPlaying(false);
          setStreamState('idle');
          setStatusMessage('Toca reproducir para continuar');
          return;
        }
        reconnectRef.current?.('La señal aún no responde');
      }
    }, delay);
  }, [clearRetry]);

  useEffect(() => {
    reconnectRef.current = reconnect;
  }, [reconnect]);

  const startPlayback = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    clearRetry();
    wantedPlayingRef.current = true;
    setIsPlaying(true);
    setStreamState('connecting');
    setStatusMessage('Conectando con Radio ASENF…');

    try {
      if (!audio.src) audio.src = STREAM_URL;
      await audio.play();
    } catch (error) {
      if (error?.name === 'NotAllowedError') {
        wantedPlayingRef.current = false;
        setIsPlaying(false);
        setStreamState('idle');
        setStatusMessage('El navegador bloqueó el audio. Toca reproducir nuevamente.');
      } else {
        reconnectRef.current?.('No pudimos abrir la señal');
      }
    }
  }, [clearRetry]);

  const stopPlayback = useCallback(() => {
    wantedPlayingRef.current = false;
    retryCountRef.current = 0;
    clearRetry();
    audioRef.current?.pause();
    setIsPlaying(false);
    setStreamState('idle');
    setStatusMessage('Transmisión pausada');
  }, [clearRetry]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onPlaying = () => {
      clearRetry();
      retryCountRef.current = 0;
      lastProgressAtRef.current = Date.now();
      setIsPlaying(true);
      setStreamState('playing');
      setStatusMessage('Señal estable · En directo');
    };
    const onWaiting = () => {
      if (!wantedPlayingRef.current) return;
      setStreamState('buffering');
      setStatusMessage('Ajustando la señal…');
    };
    const onFailure = () => reconnectRef.current?.('Reconectando la transmisión');
    const onTimeUpdate = () => {
      if (audio.currentTime !== lastMediaTimeRef.current) {
        lastMediaTimeRef.current = audio.currentTime;
        lastProgressAtRef.current = Date.now();
      }
    };

    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('stalled', onFailure);
    audio.addEventListener('ended', onFailure);
    audio.addEventListener('error', onFailure);
    audio.addEventListener('timeupdate', onTimeUpdate);

    return () => {
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('stalled', onFailure);
      audio.removeEventListener('ended', onFailure);
      audio.removeEventListener('error', onFailure);
      audio.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [clearRetry]);

  useEffect(() => {
    const watchdog = window.setInterval(() => {
      const audio = audioRef.current;
      if (!audio || !wantedPlayingRef.current || document.hidden || !navigator.onLine) return;
      const stoppedProgress = Date.now() - lastProgressAtRef.current > STALL_GRACE_MS;
      if (audio.paused || (stoppedProgress && audio.readyState < HTMLMediaElement.HAVE_FUTURE_DATA)) {
        reconnectRef.current?.('La señal se interrumpió. Recuperándola');
      }
    }, 4000);
    return () => window.clearInterval(watchdog);
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      if (wantedPlayingRef.current) reconnectRef.current?.('Conexión recuperada');
    };
    const handleOffline = () => {
      clearRetry();
      setStreamState('offline');
      setStatusMessage('Sin conexión. Reintentaremos automáticamente.');
    };
    const handleVisibility = () => {
      if (!document.hidden && wantedPlayingRef.current && audioRef.current?.paused) {
        reconnectRef.current?.('Retomando la transmisión');
      }
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [clearRetry]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.volume = isMuted ? 0 : volume / 100;
    localStorage.setItem('radio_vol', String(volume));
  }, [volume, isMuted]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const loadData = async () => {
      const results = await Promise.allSettled([
        fetch('/api/player/current', { signal: controller.signal }),
        fetch('/api/player/next-songs?limit=3', { signal: controller.signal }),
        fetch('/api/player/shuffle/status', { signal: controller.signal }),
      ]);
      if (!active) return;

      const [currentResult, nextResult, shuffleResult] = results;
      if (currentResult.status === 'fulfilled' && currentResult.value.ok) {
        setCurrentSong(await currentResult.value.json());
      }
      if (nextResult.status === 'fulfilled' && nextResult.value.ok) {
        const data = await nextResult.value.json();
        setNextSongs(data.proximas || []);
        setTotalSongs(data.total || 0);
        setShuffle(Boolean(data.shuffle));
      }
      if (shuffleResult.status === 'fulfilled' && shuffleResult.value.ok) {
        const data = await shuffleResult.value.json();
        setShuffle(Boolean(data.shuffle));
      }
    };

    loadData();
    const interval = window.setInterval(loadData, 10000);
    return () => {
      active = false;
      controller.abort();
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => () => {
    wantedPlayingRef.current = false;
    clearRetry();
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
  }, [clearRetry]);

  const fmtTime = (seconds) => {
    if (!seconds || seconds <= 0) return '--:--';
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${(Math.floor(seconds) % 60).toString().padStart(2, '0')}`;
  };

  const progressPct = currentSong?.duracion > 0
    ? Math.min((currentSong.tiempo_actual / currentSong.duracion) * 100, 100)
    : 0;
  const stateLabel = streamState === 'playing' ? 'En vivo' : streamState === 'idle' ? 'Listo' : streamState === 'offline' ? 'Sin red' : 'Conectando';

  return (
    <section className="radio-player" aria-label="Reproductor de Radio ASENF">
      <div className="radio-player__topline">
        <div className={`signal-pill signal-pill--${streamState}`}>
          <span className="signal-pill__dot" aria-hidden="true" />
          <span>{stateLabel}</span>
        </div>
        <div className="listener-count" aria-label={`${currentSong?.oyentes || 0} oyentes conectados`}>
          <Users aria-hidden="true" />
          <strong>{currentSong?.oyentes || 0}</strong>
          <span>oyentes</span>
        </div>
      </div>

      <div className="radio-player__body">
        <div className={`vinyl-stage ${isPlaying ? 'is-playing' : ''}`} aria-hidden="true">
          <div className="vinyl-disc">
            <span className="vinyl-disc__groove vinyl-disc__groove--one" />
            <span className="vinyl-disc__groove vinyl-disc__groove--two" />
            <span className="vinyl-disc__label"><Disc /></span>
          </div>
          <span className="vinyl-arm" />
        </div>

        <div className="now-playing">
          <span className="now-playing__eyebrow">Ahora suena</span>
          <h2>{currentSong?.titulo || 'Radio ASENF'}</h2>
          <p>{currentSong?.artista || 'La voz de quienes cuidan'}</p>
          {shuffle && (
            <span className="shuffle-badge"><Shuffle aria-hidden="true" /> Selección aleatoria</span>
          )}
        </div>

        <div className="track-progress" aria-label="Progreso de la canción actual">
          <div className="track-progress__rail">
            <span style={{ width: `${progressPct}%` }} />
          </div>
          <div className="track-progress__times">
            <span>{fmtTime(currentSong?.tiempo_actual)}</span>
            <span>{fmtTime(currentSong?.duracion)}</span>
          </div>
        </div>

        <button
          type="button"
          className="play-button"
          onClick={isPlaying ? stopPlayback : startPlayback}
          aria-label={isPlaying ? 'Pausar Radio ASENF' : 'Escuchar Radio ASENF'}
        >
          {isPlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
        </button>

        <p className={`stream-message stream-message--${streamState}`} aria-live="polite">
          {statusMessage}
        </p>

        {(streamState === 'offline' || streamState === 'reconnecting') && (
          <button type="button" className="retry-button" onClick={startPlayback}>
            Reintentar ahora
          </button>
        )}

        <div className="volume-control">
          <button
            type="button"
            onClick={() => setIsMuted((muted) => !muted)}
            aria-label={isMuted ? 'Activar sonido' : 'Silenciar'}
          >
            {isMuted ? <VolumeX aria-hidden="true" /> : <Volume2 aria-hidden="true" />}
          </button>
          <input
            type="range"
            min="0"
            max="100"
            value={isMuted ? 0 : volume}
            onChange={(event) => {
              setVolume(Number(event.target.value));
              setIsMuted(false);
            }}
            aria-label="Volumen"
            aria-valuetext={`${isMuted ? 0 : volume} por ciento`}
          />
          <span>{isMuted ? 0 : volume}</span>
        </div>

        {nextSongs.length > 0 && (
          <div className="up-next">
            <div className="up-next__heading">
              <span>A continuación</span>
              <span><Music aria-hidden="true" /> {totalSongs} canciones</span>
            </div>
            <ol>
              {nextSongs.slice(0, 3).map((song, index) => (
                <li key={`${song.id}-${index}`}>
                  <span className="up-next__number">{String(index + 1).padStart(2, '0')}</span>
                  <span className="up-next__song">
                    <strong>{song.titulo}</strong>
                    <small>{song.artista || 'Artista por confirmar'}</small>
                  </span>
                  {song.duracion > 0 && <time>{fmtTime(song.duracion)}</time>}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      <audio ref={audioRef} src={STREAM_URL} preload="none" playsInline />
    </section>
  );
}
